import React from "react"
import Layout from "../components/layout"
import { useStaticQuery, graphql } from "gatsby"
import Img from "gatsby-image"
import { Text, Heading, Box, Flex, Link } from "rebass"
import Page from "../components/page"
import CenterPage from "../components/center-page"
import BlockContent from "@sanity/block-content-to-react"
import imageUrlBuilder from "@sanity/image-url"
import sanityClient from "@sanity/client"

const client = sanityClient({
  projectId: "08q4tol2",
  dataset: "production",
  token: "",
  useCdn: false,
})

const builder = imageUrlBuilder(client)

function urlFor(source) {
  return builder.image(source)
}

const kebabCase = str =>
  str
    .match(/[A-Z]{2,}(?=[A-Z][a-z0-9]*|\b)|[A-Z]?[a-z0-9]*|[A-Z]|[0-9]+/g)
    .filter(Boolean)
    .map(x => x.toLowerCase())
    .join("-")

const SongTitle = ({ title, bug }) => (
  <Heading
    fontSize={[5, 6]}
    my={0}
    lineHeight={1}
    fontStyle="italic"
    color="#fffdba"
    fontWeight="700"
  >
    {title}
    <Text as="p" fontSize={4}>
      {bug}
    </Text>
  </Heading>
)

const IndexPage = () => {
  const data = useStaticQuery(graphql`
    query {
      allSanityPage {
        edges {
          node {
            id
            title
            subtitle
            order
            centerAligned
            showTitle
            _rawLyrics
            _rawCredits
            photo {
              asset {
                id
                fluid(maxWidth: 1700) {
                  ...GatsbySanityImageFluid
                }
              }
              hotspot {
                x
                y
              }
            }
          }
        }
      }
    }
  `)

  const sorted = data.allSanityPage.edges.sort(function(a, b) {
    return a.node.order > b.node.order ? 1 : -1
  })

  return (
    <Layout>
      {sorted.map((edge, index) => {
        const next = sorted[index + 1]
        const { photo } = edge.node
        const { asset } = photo
        const imageUrl = urlFor(asset.id)
          .width(1000)
          .fit("crop")
          .focalPoint(
            photo.hotspot?.focalPoint ? photo.hotspot.focalPoint.x : 0,
            photo.hotspot?.focalPoint ? photo.hotspot.focalPoint.y : 0
          )
          .url()

        if (edge.node.centerAligned) {
          return (
            <CenterPage
              key={edge.node.order}
              anchor={kebabCase(edge.node.title)}
              nextLink={next ? kebabCase(next.node.title) : null}
              title={
                edge.node.showTitle && (
                  <SongTitle title={edge.node.title} bug={edge.node.subtitle} />
                )
              }
              mobilePhoto={<Img fluid={edge.node.photo.asset.fluid} />}
              photo={imageUrl}
            />
          )
        } else if (edge.node.order === 1) {
          return (
            <Page
              anchor={kebabCase(edge.node.title)}
              nextLink={next ? kebabCase(next.node.title) : null}
              key={edge.node.order}
              mobilePhoto={<img width="100%" src={imageUrl} />}
              photo={
                <Box
                  sx={{
                    backgroundImage: `url(${imageUrl})`,
                    width: "100%",
                    height: "100%",
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                  }}
                />
              }
              title={
                <SongTitle title={edge.node.title} bug={edge.node.subtitle} />
              }
              lyrics={<BlockContent blocks={edge.node._rawLyrics} />}
              credits={
                <Box>
                  <Link
                    display="block"
                    color="inherit"
                    fontSize={3}
                    href="http://instagram.com/brawerMD"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: "none" }}
                    mb={3}
                  >
                    <Flex alignItems="center">
                      <i className="fab fa-instagram fa-2x fa-fw"></i>
                      <Text ml={2}>Instagram</Text>
                    </Flex>
                  </Link>
                  <Link
                    display="block"
                    color="inherit"
                    fontSize={3}
                    href="https://www.youtube.com/channel/UC-Ubaz7gd419ONJ7Vfn4RfQ"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: "none" }}
                    mb={3}
                  >
                    <Flex alignItems="center">
                      <i className="fab fa-youtube fa-2x fa-fw"></i>
                      <Text ml={2}>YouTube</Text>
                    </Flex>
                  </Link>
                  <Link
                    display="block"
                    color="inherit"
                    fontSize={3}
                    href="https://linktr.ee/thebesttime"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: "none" }}
                    mb={3}
                  >
                    <Flex alignItems="center">
                      <i className="fab fa-spotify fa-2x fa-fw"></i>
                      <Text ml={2}>Spotify</Text>
                    </Flex>
                  </Link>
                  <Link
                    display="block"
                    color="inherit"
                    fontSize={3}
                    href="https://linktr.ee/thebesttime"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: "none" }}
                    mb={3}
                  >
                    <Flex alignItems="center">
                      <i className="fab fa-itunes fa-2x fa-fw"></i>
                      <Text ml={2}>Apple Music</Text>
                    </Flex>
                  </Link>
                </Box>
              }
            ></Page>
          )
        } else {
          return (
            <Page
              anchor={kebabCase(edge.node.title)}
              nextLink={next ? kebabCase(next.node.title) : null}
              key={edge.node.order}
              mobilePhoto={<img width="100%" src={imageUrl} />}
              photo={
                <Box
                  sx={{
                    backgroundImage: `url(${imageUrl})`,
                    width: "100%",
                    height: "100%",
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                  }}
                />
              }
              title={
                <SongTitle title={edge.node.title} bug={edge.node.subtitle} />
              }
              lyrics={<BlockContent blocks={edge.node._rawLyrics} />}
              credits={<BlockContent blocks={edge.node._rawCredits} />}
            ></Page>
          )
        }
      })}
    </Layout>
  )
}

export default IndexPage
